/**
 * Toasts.
 *
 * A plain module store rather than a context, for one reason that decides the
 * whole shape: the thing that most needs to raise a toast is the RTK Query
 * middleware, which is not a component and cannot call a hook. Anything that
 * can `import` can call `toast.error(...)` — a middleware, an event handler, a
 * `catch` in a helper — and the viewport subscribes from React.
 *
 * ---------------------------------------------------------------------------
 * What belongs in a toast, and what does not.
 *
 * A toast is for something that happened *outside the user's line of sight*: a
 * background load that failed, a save that succeeded while they had already
 * scrolled on. It is the wrong place for anything they need to read carefully
 * or act on precisely, because it is gone in five seconds and it covers part
 * of the screen while it is there.
 *
 * So: failed **queries** toast, because a background load has nowhere else to
 * report itself. Failed **mutations** do not — they are always tied to a
 * control the person just clicked, and the form beside that control says what
 * went wrong and keeps saying it. Two reports of the same failure is worse
 * than one, and the one that vanishes is the one to drop.
 * ---------------------------------------------------------------------------
 */

export type ToastTone = "error" | "success" | "info";

export interface ToastAction {
  label: string;
  onSelect: () => void;
}

export interface ToastOptions {
  title: string;
  /** One sentence at most. Anything longer belongs on the page. */
  description?: string;
  tone?: ToastTone;
  /** Milliseconds on screen. Every toast leaves on its own. */
  duration?: number;
  action?: ToastAction;
}

export interface ToastRecord extends Required<Pick<ToastOptions, "title" | "tone">> {
  id: string;
  description?: string;
  duration: number;
  action?: ToastAction;
  /**
   * How many times this same toast has been raised while it was on screen.
   *
   * When the API is down every query on the page fails at once. Six identical
   * toasts stacked up is not six pieces of information — it is one, told
   * badly, filling the screen. They collapse into one with a count.
   */
  count: number;
}

/**
 * Long enough to read twice. Errors get the longest, since they usually say
 * what to do next — but they still leave on their own: a toast that waits for
 * a click piles up behind every dialog and covers the page beneath it.
 */
const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  error: 8000,
};

/** More than this on screen is a wall, not a notification. */
const MAX_VISIBLE = 4;

type Listener = (toasts: ToastRecord[]) => void;

let toasts: ToastRecord[] = [];
const listeners = new Set<Listener>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

let sequence = 0;

function emit(): void {
  // A new array each time: the viewport uses `useSyncExternalStore`, which
  // compares by reference and would skip a mutation in place.
  const snapshot = toasts;
  for (const listener of listeners) listener(snapshot);
}

function schedule(record: ToastRecord): void {
  clearTimer(record.id);
  timers.set(
    record.id,
    setTimeout(() => {
      dismiss(record.id);
    }, record.duration),
  );
}

function clearTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

/** Two toasts are "the same" when they would read identically. */
function isSame(a: ToastRecord, b: ToastOptions & { tone: ToastTone }): boolean {
  return a.tone === b.tone && a.title === b.title && a.description === b.description;
}

export function show(options: ToastOptions): string {
  const tone = options.tone ?? "info";
  const existing = toasts.find((candidate) => isSame(candidate, { ...options, tone }));

  if (existing) {
    // Bump the count and restart the clock rather than stacking a duplicate.
    toasts = toasts.map((candidate) =>
      candidate.id === existing.id ? { ...candidate, count: candidate.count + 1 } : candidate,
    );
    const updated = toasts.find((candidate) => candidate.id === existing.id);
    if (updated) schedule(updated);
    emit();
    return existing.id;
  }

  sequence += 1;
  const record: ToastRecord = {
    id: `toast-${String(sequence)}`,
    title: options.title,
    description: options.description,
    tone,
    duration: options.duration ?? DEFAULT_DURATION[tone],
    action: options.action,
    count: 1,
  };

  // Oldest out first. The newest is the one that describes what just happened.
  toasts = [...toasts, record].slice(-MAX_VISIBLE);
  schedule(record);
  emit();

  return record.id;
}

export function dismiss(id: string): void {
  clearTimer(id);
  toasts = toasts.filter((candidate) => candidate.id !== id);
  emit();
}

export function dismissAll(): void {
  for (const id of timers.keys()) clearTimeout(timers.get(id));
  timers.clear();
  toasts = [];
  emit();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): ToastRecord[] {
  return toasts;
}

export const toast = {
  error: (title: string, options: Omit<ToastOptions, "title" | "tone"> = {}) =>
    show({ ...options, title, tone: "error" }),
  success: (title: string, options: Omit<ToastOptions, "title" | "tone"> = {}) =>
    show({ ...options, title, tone: "success" }),
  info: (title: string, options: Omit<ToastOptions, "title" | "tone"> = {}) =>
    show({ ...options, title, tone: "info" }),
  show,
  dismiss,
  dismissAll,
};
